class ProductCardColorSwap {
  constructor() {
    this.mobileQuery = window.matchMedia('(max-width: 767px)');
    this.limits = { desktop: 10, mobile: 5, mobileMin: 4 };
    this.layoutFrame = null;
    this.init();
  }

  init() {
    // Kart ilk yüklemede gizli bir alandaysa (slider, sekme) görünür olunca hesapla
    // WeakSet: kopyalanan kartlar (slider loop) data attribute'u da kopyalar, gözlenmemiş sayılmalı
    this.observed = new WeakSet();
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => this.layout(entry.target));
      });
    }

    this.layoutAll(document);

    // Masaüstü: renk üzerine gelince geçici önizleme, ayrılınca seçili renge dön
    document.body.addEventListener('mouseover', (e) => {
      if (this.mobileQuery.matches) return;
      const swatch = e.target.closest?.('[data-color-variant]');
      if (!swatch || swatch.contains(e.relatedTarget)) return;
      this.preview(swatch);
    });

    document.body.addEventListener('mouseout', (e) => {
      if (this.mobileQuery.matches) return;
      const swatch = e.target.closest?.('[data-color-variant]');
      if (!swatch || swatch.contains(e.relatedTarget)) return;
      const card = swatch.closest('[data-product-info]');
      const active = card?.querySelector('[data-color-variant].is-active');
      if (active) this.preview(active);
    });

    // Renge basınca listelemeden ayrılmadan görseli değiştir; seçili renge tekrar basınca ürüne git
    document.body.addEventListener('click', (e) => {
      const swatch = e.target.closest?.('[data-color-variant]');
      if (!swatch) return;
      if (swatch.classList.contains('is-active')) return;
      e.preventDefault();
      this.select(swatch);
    });

    const relayout = () => {
      window.cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = window.requestAnimationFrame(() => this.layoutAll(document));
    };
    window.addEventListener('resize', relayout);
    document.addEventListener('collection:reloaded', relayout);
    document.addEventListener('shopify:section:load', relayout);

    // Sonsuz kaydırma / filtre ile gelen kartlar
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) this.layoutAll(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  layoutAll(root) {
    const containers = [];
    if (root.matches?.('[data-color-container]')) containers.push(root);
    if (root.querySelectorAll) containers.push(...root.querySelectorAll('[data-color-container]'));

    containers.forEach((container) => {
      if (this.resizeObserver && !this.observed.has(container)) {
        this.observed.add(container);
        this.resizeObserver.observe(container);
      }
      this.layout(container);
    });
  }

  // Masaüstünde en fazla 10, mobilde sığma durumuna göre 4-5 renk; kalanı "+N"
  layout(container) {
    const items = [...container.querySelectorAll('[data-color-variant]')];
    const more = container.querySelector('[data-color-more]');
    if (!items.length || !more) return;

    const available = container.clientWidth;
    if (!available) return;

    const isMobile = this.mobileQuery.matches;
    const max = isMobile ? this.limits.mobile : this.limits.desktop;
    const min = isMobile ? this.limits.mobileMin : 1;
    const total = parseInt(container.dataset.total, 10) || items.length;

    const style = getComputedStyle(container);
    const gap = parseFloat(style.columnGap) || 0;
    const innerWidth = available - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    const itemWidth = items[0].getBoundingClientRect().width;

    // "+N" yazısı ölçülürken görünür olmalı
    container.classList.add('is-ready');
    more.classList.add('is-visible');

    let visible = Math.min(items.length, max);
    while (visible > min) {
      const rest = total - visible;
      let needed = visible * itemWidth + (visible - 1) * gap;
      if (rest > 0) {
        more.textContent = `+${rest}`;
        needed += gap + more.getBoundingClientRect().width;
      }
      if (needed <= innerWidth) break;
      visible--;
    }

    const rest = total - visible;
    more.textContent = rest > 0 ? `+${rest}` : '';
    more.classList.toggle('is-visible', rest > 0);

    items.forEach((item, index) => {
      // Seçili renk gizli kalırsa kullanıcı neyin seçili olduğunu göremez; onu son görünen yere koy
      item.classList.toggle('is-overflow', index >= visible);
    });
    const active = container.querySelector('[data-color-variant].is-active.is-overflow');
    if (active && visible > 0) {
      items[visible - 1].classList.add('is-overflow');
      active.classList.remove('is-overflow');
    }
  }

  select(swatch) {
    const card = swatch.closest('[data-product-info]');
    if (!card) return;

    card.querySelectorAll('[data-color-variant].is-active').forEach((item) => {
      item.classList.remove('is-active');
      item.removeAttribute('aria-current');
    });
    swatch.classList.add('is-active');
    swatch.setAttribute('aria-current', 'true');

    const isOriginal = swatch === card.querySelector('[data-color-variant]');

    // Kart görseline/başlığına basınca seçilen renge gidilsin
    card.querySelectorAll('a.main-card-image, a.product-card-mobile-gallery__slide, .product-card-title a[href]').forEach((link) => {
      if (link.dataset.originalHref === undefined) link.dataset.originalHref = link.getAttribute('href');
      link.setAttribute('href', isOriginal ? link.dataset.originalHref : swatch.getAttribute('href'));
    });

    // Mobil galeride ilk görsele dön ki seçilen renk görünsün
    const track = card.querySelector('[data-product-card-mobile-gallery-track]');
    if (track && track.scrollLeft > 0) track.scrollTo({ left: 0, behavior: 'smooth' });

    this.preview(swatch);

    const container = swatch.closest('[data-color-container]');
    if (container) this.layout(container);
  }

  preview(swatch) {
    const card = swatch.closest('[data-product-info]');
    if (!card) return;

    const media = card.querySelector('.product-card-media');
    const images = card.querySelectorAll('.product-card--img, [data-mobile-product-card-gallery-first-image]');
    const isOriginal = swatch === card.querySelector('[data-color-variant]');
    const src = swatch.dataset.hoverSrc;

    if (isOriginal || !src) {
      media?.classList.remove('is-color-preview');
      images.forEach((image) => this.restoreImage(image));
      return;
    }

    // Ana medya video ise renk önizlemesi boyunca videoyu gizle
    media?.classList.add('is-color-preview');
    images.forEach((image) => this.setImage(image, src));
  }

  setImage(image, src) {
    if (image.dataset.originalSrc === undefined) {
      image.dataset.originalSrc = image.getAttribute('src') || '';
      image.dataset.originalSrcset = image.getAttribute('srcset') || '';
    }
    image.srcset = this.generateSrcset(src.split('?')[0], src);
    image.src = src;
  }

  restoreImage(image) {
    if (image.dataset.originalSrc === undefined) return;
    if (image.dataset.originalSrcset) {
      image.srcset = image.dataset.originalSrcset;
    } else {
      image.removeAttribute('srcset');
    }
    image.src = image.dataset.originalSrc;
  }

  generateSrcset(baseUrl, originalUrl) {
    const widths = [165, 360, 533, 720, 940, 1066];
    const versionMatch = originalUrl.match(/v=(\d+)/);
    const version = versionMatch ? versionMatch[1] : '';

    return widths.map(width => {
      const url = version
        ? `${baseUrl}?v=${version}&width=${width}`
        : `${baseUrl}?width=${width}`;
      return `${url} ${width}w`;
    }).join(',');
  }
}

class ProductCardMobileGallery {
  constructor() {
    this.init();
  }

  init() {
    this.initialize(document);

    document.addEventListener('collection:reloaded', () => this.initialize(document));
    document.addEventListener('shopify:section:load', () => this.initialize(document));
    window.addEventListener('resize', () => this.initialize(document));

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => this.initialize(node));
      });
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  initialize(root) {
    const galleries = [];
    if (root instanceof Element && root.matches('[data-product-card-mobile-gallery]')) {
      galleries.push(root);
    }
    if (root.querySelectorAll) {
      galleries.push(...root.querySelectorAll('[data-product-card-mobile-gallery]'));
    }

    galleries.forEach((gallery) => {
      if (gallery.dataset.galleryInitialized === 'true') return;

      const track = gallery.querySelector('[data-product-card-mobile-gallery-track]');
      const progressFill = gallery.querySelector('.product-card-mobile-gallery__progress-fill');
      if (!track || !progressFill) return;

      const slideCount = track.children.length;
      const updateProgress = () => {
        const maximumScroll = track.scrollWidth - track.clientWidth;
        const scrollProgress = maximumScroll > 0 ? track.scrollLeft / maximumScroll : 0;
        const segmentWidth = 100 / slideCount;
        const leftPosition = scrollProgress * (100 - segmentWidth);

        progressFill.style.left = `${leftPosition}%`;
      };

      let frameId;
      track.addEventListener('scroll', () => {
        window.cancelAnimationFrame(frameId);
        frameId = window.requestAnimationFrame(updateProgress);
      }, { passive: true });

      window.addEventListener('resize', updateProgress);
      window.requestAnimationFrame(updateProgress);

      gallery.dataset.galleryInitialized = 'true';
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductCardColorSwap();
    new ProductCardMobileGallery();
  });
} else {
  new ProductCardColorSwap();
  new ProductCardMobileGallery();
}
